import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Accordion, AccordionActions, AccordionDetails } from '@components/primitives/Accordion';
import { Avatar } from '@jotpsych/ui';
import { Button } from '@components/primitives/Button';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { formatAppointmentApprovedNotification } from '@features/appointments/utils/appointmentNotifications';
import { EligibilityBadge } from '@features/eligibility-checks';
import { useSubjectLanguageVariants } from '@features/user';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { formatPatientName } from '@utils/patientUtils';
import { useSnackbar } from 'notistack';

import { useNavigateToEncounter } from '../../hooks/useNavigateToEncounter';
import { useOrgScheduleFilters } from '../../hooks/useOrgScheduleFilters';
import { useProviderNameMap } from '../../hooks/useProviderNameMap';
import { useScheduleReadOnly } from '../../hooks/useScheduleReadOnly';
import {
  setEditingAppointmentId,
  useDeleteAppointmentMutation,
  useRejectAppointmentMutation,
  useUpdateAppointmentMutation,
} from '../../store';
import { NoShowAction } from '../NoShowAction';
import { PatientConfirmedStatus } from '../PatientConfirmedStatus';

import { ActionLink } from './components/ActionLink';
import { PatientClinicalDetails } from './components/PatientClinicalDetails';
import {
  ActionButtonWrapper,
  ActionLinksContainer,
  AvatarElement,
  AvatarTimeWrapper,
  ExpandedContentContainer,
  PatientNameText,
  PatientNameWrapper,
  StyledAccordionSummary,
  SummaryContent,
  TimeNameWrapper,
} from './AppointmentCard.styles';
import { type AppointmentCardProps } from './AppointmentCard.types';
import {
  extractAppointmentData,
  formatTimeRange,
  getAppointmentState,
  getButtonConfig,
} from './AppointmentCard.utils';

/**
 * Appointment card component displaying patient information and session details
 * Uses Accordion primitives for expandable content with responsive layout
 */
export const AppointmentCard: React.FC<AppointmentCardProps> = props => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { appointment: appointmentTracking } = useEventTracking();
  const { navigateToEncounter, isLoading: isCreatingEncounter } = useNavigateToEncounter();
  const { singularCap: subjectLanguage } = useSubjectLanguageVariants();
  const [deleteAppointment] = useDeleteAppointmentMutation();
  const [updateAppointment, { isLoading: isAccepting }] = useUpdateAppointmentMutation();
  const [rejectAppointment, { isLoading: isRejecting }] = useRejectAppointmentMutation();

  const isReadOnly = useScheduleReadOnly();
  const { isOrgViewActive } = useOrgScheduleFilters();

  // State for cancel dialog (only used when no parent handler)
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const {
    appointment,
    expanded = false,
    onChange,
    onRecordSession,
    onViewSession,
    onViewProfile,
    onCancel,
    analytics,
    navigateOptions,
    // onMarkNoShow, // Commented out - feature not ready
  } = props;

  // Extract data from appointment
  const appointmentData = extractAppointmentData(appointment);
  const {
    firstName,
    lastName,
    preferredName,
    patientId,
    appointmentTime,
    appointmentType,
    encounterNoteTitle,
  } = appointmentData;

  const providerNameMap = useProviderNameMap();
  const providerName = providerNameMap.get(appointment.provider_id ?? '');

  // Format display values with fallback to encounter note title
  const patientDisplayName =
    firstName || lastName
      ? formatPatientName(firstName || '', lastName || '', preferredName)
      : encounterNoteTitle || `Unknown ${subjectLanguage}`;
  const timeRange = appointmentTime
    ? formatTimeRange(appointmentTime.start, appointmentTime.end)
    : '';

  // Determine button state and configuration (memoized to prevent unnecessary re-calculations)

  const appointmentState = useMemo(
    () => getAppointmentState(appointment),
    // Only depend on specific fields to avoid recalculating on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointment.start_time, appointment.duration],
  );

  const buttonConfig = useMemo(() => getButtonConfig(appointmentState), [appointmentState]);

  // Determine if this is a non-imminent suggested appointment (buttons in summary area)
  const isNonImminentSuggested =
    appointment.status === 'suggested' &&
    (appointmentState === 'past' || appointmentState === 'future');

  // Track expansion/collapse
  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (analytics?.source) {
      if (isExpanded) {
        appointmentTracking.cardExpanded({
          appointment_id: appointment.appointment_id,
          source: analytics.source,
          position: analytics.position,
          is_org_schedule: isOrgViewActive,
        });
      } else {
        appointmentTracking.cardCollapsed({
          appointment_id: appointment.appointment_id,
          source: analytics.source,
          is_org_schedule: isOrgViewActive,
        });
      }
    }
    onChange?.(event, isExpanded);
  };

  // Handle session button click
  const handleSessionClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Track the action
    if (analytics?.source) {
      appointmentTracking.actionClicked({
        appointment_id: appointment.appointment_id,
        action: buttonConfig.action === 'view' ? 'view_session' : 'record_session',
        source: analytics.source,
        has_encounter: !!appointment.encounter,
        is_org_schedule: isOrgViewActive,
      });
    }

    try {
      // Navigate to encounter (creating if necessary)
      await navigateToEncounter(appointment, navigateOptions);

      // Call appropriate handler for tracking
      if (buttonConfig.action === 'view' && onViewSession) {
        onViewSession();
      } else if (onRecordSession) {
        onRecordSession();
      }
    } catch (error) {
      console.error('Failed to navigate to encounter:', error);
      enqueueSnackbar('Failed to navigate to encounter. Please try again.', {
        variant: 'error',
      });
    }
  };

  // Handle view profile click
  const handleViewProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (analytics?.source) {
      appointmentTracking.actionClicked({
        appointment_id: appointment.appointment_id,
        action: 'view_profile',
        source: analytics.source,
        is_org_schedule: isOrgViewActive,
      });
    }

    if (patientId) {
      navigate(`/patient/${patientId}`);
      onViewProfile?.();
    }
  };

  // Handle cancel/delete - conditional dialog for suggested
  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (analytics?.source) {
      appointmentTracking.actionClicked({
        appointment_id: appointment.appointment_id,
        action: appointment.status === 'suggested' ? 'suggestion_reject' : 'cancel',
        source: analytics.source,
        is_org_schedule: isOrgViewActive,
      });
    }

    // For suggested appointments: reject immediately without dialog
    if (appointment.status === 'suggested') {
      void performCancel();
    } else {
      // For confirmed appointments: show confirmation dialog
      setShowCancelDialog(true);
    }
  };

  // Handle accept action for suggested appointments
  const handleAcceptClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (analytics?.source) {
      appointmentTracking.actionClicked({
        appointment_id: appointment.appointment_id,
        action: 'suggestion_accept',
        source: analytics.source,
        is_org_schedule: isOrgViewActive,
      });
    }

    try {
      const updatedAppointment = await updateAppointment({
        appointmentId: appointment.appointment_id,
        appointmentUpdate: {}, // Empty payload accepts appointment with current values
      }).unwrap();

      // Show success notification
      const notificationData = formatAppointmentApprovedNotification(updatedAppointment);
      enqueueSnackbar('Appointment Approved!', {
        variant: 'success',
        data: notificationData,
      });
    } catch (error) {
      console.error('Failed to approve appointment:', error);
      enqueueSnackbar('Failed to approve appointment. Please try again.', {
        variant: 'error',
      });
    }
  };

  // Handle modify action for suggested appointments - opens edit modal
  const handleModifyClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (analytics?.source) {
      appointmentTracking.actionClicked({
        appointment_id: appointment.appointment_id,
        action: 'reschedule', // Reusing 'reschedule' action for modify
        source: analytics.source,
        is_org_schedule: isOrgViewActive,
      });
    }

    dispatch(setEditingAppointmentId(appointment.appointment_id));
  };

  // Perform the actual cancellation/rejection
  const performCancel = async () => {
    try {
      // For suggested appointments: reject directly
      if (appointment.status === 'suggested') {
        await rejectAppointment({
          appointmentId: appointment.appointment_id,
          appointmentReject: {}, // Empty payload as per API spec
        }).unwrap();

        // Show success notification
        enqueueSnackbar('Suggested Appointment Rejected', {
          variant: 'error',
        });

        // RTK Query will automatically update cache
        return;
      }

      // For confirmed appointments: use existing cancel flow
      if (onCancel) {
        // Parent component handles it (e.g., for recurring appointments)
        onCancel();
        setShowCancelDialog(false);
      } else {
        // Handle it ourselves
        await deleteAppointment({
          appointmentId: appointment.appointment_id,
        }).unwrap();
        setShowCancelDialog(false);
        // RTK Query will automatically update the cache
      }
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      enqueueSnackbar('Failed to cancel appointment. Please try again.', {
        variant: 'error',
      });
      setShowCancelDialog(false);
    }
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={
          appointment.status === 'suggested'
            ? {
                border: 'none',
                outline: '2px',
                outlineStyle: 'dashed',
                outlineColor: 'primary.main',
                outlineOffset: '-2px',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <StyledAccordionSummary>
          <SummaryContent>
            {/* Avatar - always in first column for narrow/medium */}
            <AvatarElement>
              <Avatar
                id={patientId}
                firstName={firstName}
                lastName={lastName}
                sx={{ width: 45, height: 45 }}
              />
            </AvatarElement>

            {/* Time and Name stacked for narrow/medium views */}
            <TimeNameWrapper>
              {timeRange && (
                <Typography variant="body2" color="text.secondary">
                  {timeRange}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PatientNameText>{patientDisplayName}</PatientNameText>
                <EligibilityBadge
                  patientId={patientId}
                  source="appointment_card"
                  showActionLabel={false}
                />
              </Box>
              {providerName && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                  {providerName}
                </Typography>
              )}
              {appointmentType && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    '@container (max-width: 500px)': {
                      wordBreak: 'normal',
                      overflowWrap: 'break-word',
                    },
                    '@container (min-width: 501px)': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  {appointmentType}
                </Typography>
              )}
            </TimeNameWrapper>

            {/* Avatar and Time together for wide view */}
            <AvatarTimeWrapper>
              <Avatar
                id={patientId}
                firstName={firstName}
                lastName={lastName}
                sx={{ width: 45, height: 45 }}
              />
              {timeRange && (
                <Typography variant="body2" color="text.secondary">
                  {timeRange}
                </Typography>
              )}
            </AvatarTimeWrapper>

            {/* Patient name alone for wide view */}
            <PatientNameWrapper>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {patientDisplayName}
                </Typography>
                <EligibilityBadge
                  patientId={patientId}
                  source="appointment_card"
                  showActionLabel={false}
                />
              </Box>
              {providerName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.8125rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {providerName}
                </Typography>
              )}
              {appointmentType && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {appointmentType}
                </Typography>
              )}
            </PatientNameWrapper>

            {/* Action Button - hidden in read-only mode */}
            {!isReadOnly && (
              <ActionButtonWrapper>
                {isNonImminentSuggested ? (
                  // Suggested appointment that's not imminent: show Reject + Modify + Accept buttons
                  <Box
                    sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}
                  >
                    {/* Right: Modify + Accept buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={handleModifyClick}
                        disabled={isAccepting || isRejecting}
                      >
                        Modify
                      </Button>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleAcceptClick}
                        disabled={isAccepting || isRejecting}
                      >
                        {isAccepting ? <CircularProgress size={20} color="inherit" /> : 'Accept'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  // Normal appointment or imminent suggested: show session button
                  <Button
                    variant={buttonConfig.variant}
                    size="large"
                    onClick={handleSessionClick}
                    disabled={isCreatingEncounter}
                    data-testid={`appointment-session-button-${appointment.appointment_id}`}
                  >
                    {isCreatingEncounter ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      buttonConfig.label
                    )}
                  </Button>
                )}
              </ActionButtonWrapper>
            )}
          </SummaryContent>
        </StyledAccordionSummary>

        <AccordionDetails>
          <ExpandedContentContainer>
            {/* Action Links - Positioned at top on narrow, bottom on wide */}
            <ActionLinksContainer>
              {/* Patient confirmed status */}
              <PatientConfirmedStatus appointment={appointment} />

              {/* No-show status or action link - hidden in read-only mode */}
              {!isReadOnly && (appointmentState === 'past' || appointmentState === 'active') && (
                <NoShowAction
                  appointment={appointment}
                  onMarkNoShowClick={() => {
                    if (analytics?.source) {
                      appointmentTracking.actionClicked({
                        appointment_id: appointment.appointment_id,
                        action: 'mark_no_show',
                        source: analytics.source,
                        is_org_schedule: isOrgViewActive,
                      });
                    }
                  }}
                />
              )}

              {!isReadOnly && !appointment.no_showed && (
                <>
                  <Link
                    component={RouterLink}
                    to={`/schedule?edit=${appointment.appointment_id}&date=${appointment.start_time}`}
                    onClick={e => {
                      e.stopPropagation();
                      if (analytics?.source) {
                        appointmentTracking.actionClicked({
                          appointment_id: appointment.appointment_id,
                          action: 'reschedule',
                          source: analytics.source,
                          is_org_schedule: isOrgViewActive,
                        });
                      }
                    }}
                    variant="body2"
                    underline="hover"
                    sx={{ cursor: 'pointer' }}
                  >
                    Reschedule
                  </Link>
                  <ActionLink
                    label={appointment.status === 'suggested' ? 'Remove' : 'Cancel'}
                    onClick={handleCancelClick}
                  />
                </>
              )}
            </ActionLinksContainer>

            {/* {subjectLanguage} Clinical Details - fetched on expand */}
            {patientId && <PatientClinicalDetails patientId={patientId} expanded={expanded} />}
          </ExpandedContentContainer>
        </AccordionDetails>

        {patientId && (
          <AccordionActions
            sx={
              appointment.status === 'suggested'
                ? theme => ({
                    backgroundColor: 'primary.main',
                    borderTop: 'none',
                    color: 'primary.contrastText',
                    marginLeft: 0,
                    marginRight: 0,
                    paddingLeft: theme.spacing(2),
                    paddingRight: theme.spacing(2),
                    justifyContent: 'flex-start',
                    '@container accordion (min-width: 501px)': {
                      marginLeft: 0,
                      marginRight: 0,
                      paddingLeft: theme.spacing(3),
                      paddingRight: theme.spacing(3),
                    },
                  })
                : { justifyContent: 'flex-start' }
            }
          >
            {appointment.status === 'suggested' ? (
              // Check if buttons are in summary area (non-imminent) or footer area (imminent)
              isNonImminentSuggested ? (
                // Non-imminent: buttons in summary, show only informational footer
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <AutoAwesomeIcon sx={{ color: 'primary.contrastText', fontSize: 20 }} />
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'primary.contrastText' }}
                    >
                      Suggested Appointment
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'primary.contrastText', opacity: 0.9 }}
                    >
                      This appointment was auto-created from a recent encounter.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                // Imminent (active/upcoming): buttons NOT in summary, show footer with Approve button
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    gap: 1.5,
                    justifyContent: 'space-between',
                    color: 'primary.contrastText',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AutoAwesomeIcon sx={{ color: 'primary.contrastText', fontSize: 20 }} />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'primary.contrastText' }}
                      >
                        Suggested Appointment
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: 'primary.contrastText', opacity: 0.9 }}
                      >
                        This appointment was auto-created from a recent encounter.
                      </Typography>
                    </Box>
                  </Box>
                  {!isReadOnly && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="text"
                        size="medium"
                        onClick={handleModifyClick}
                        disabled={isAccepting || isRejecting}
                        sx={{
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&.Mui-disabled': {
                            color: 'primary.contrastText',
                            opacity: 0.6,
                          },
                        }}
                      >
                        Modify
                      </Button>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={handleAcceptClick}
                        disabled={isAccepting || isRejecting}
                        sx={{
                          color: 'primary.contrastText',
                          borderColor: 'primary.contrastText',
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderColor: 'primary.contrastText',
                          },
                          '&.Mui-disabled': {
                            color: 'primary.contrastText',
                            borderColor: 'primary.contrastText',
                            opacity: 0.6,
                          },
                        }}
                      >
                        {isAccepting ? 'Accepting...' : 'Accept'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )
            ) : (
              <Link
                component="button"
                variant="body2"
                underline="hover"
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
                onClick={handleViewProfileClick}
              >
                Full {subjectLanguage} Profile
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Link>
            )}
          </AccordionActions>
        )}
      </Accordion>

      {/* Cancel confirmation dialog - always rendered */}
      <ConfirmDialog
        open={showCancelDialog}
        variant="warning"
        title={`Remove This ${appointment.status === 'suggested' ? 'Suggested ' : ''}Appointment?`}
        message={`Are you sure you want to remove this ${
          appointment.status === 'suggested' ? 'suggested ' : ''
        }appointment from your calendar?`}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={performCancel}
        confirmText="Yes, Remove"
      />
    </>
  );
};
